import type { BrandUser, BrandUserState, RegistrationData } from '@/types'
import brandUserService from '../services/brandUserService'
import { logError } from '../utils/sentry'
import useAccessStore from './accessStore'
import { createPersistedStore } from './baseStore'

interface RegistrationStepOneResult {
  userId: string
}

interface RegistrationStepTwoResult {
  success: boolean
  message?: string
  [key: string]: unknown
}

interface BrandUserActions {
  setValue: (key: string, value: unknown) => void
  clearErrors: () => void
  registrationStepOne: (userData: RegistrationData) => Promise<RegistrationStepOneResult>
  registrationStepTwo: (isDraft?: boolean) => Promise<RegistrationStepTwoResult | null>
  fetchBrandUser: () => Promise<BrandUser>
  updateBrandUser: (userData: Partial<BrandUser>) => Promise<BrandUser>
}

const initialState: BrandUserState = {
  brandUser: null,
  error: [],
  loading: false,
  id: null,
}

type SetState<T> = (state: T | Partial<T> | ((state: T) => T | Partial<T>), replace?: boolean) => void

type GetState<T> = () => T

const actions = (set: SetState<BrandUserState>, get: GetState<BrandUserState & BrandUserActions>): BrandUserActions => ({
  setValue: (key, value) => {
    set(
      (state: BrandUserState) =>
        ({
          ...state,
          brandUser: {
            ...state.brandUser,
            [key]: value,
          },
        }) as BrandUserState,
    )
  },

  clearErrors: () => {
    set({ error: [] })
  },

  registrationStepOne: async (userData) => {
    try {
      set({ loading: true, error: [] })
      const result = await brandUserService.registerStepOne(userData)

      if (result?.userId) {
        set(
          (state) =>
            ({
              ...state,
              id: result.userId,
              loading: false,
              brandUser: {
                ...state.brandUser,
                id: result.userId,
                email: userData.email,
              },
            }) as BrandUserState,
        )
      } else {
        logError('No userId in registration result', { result })
        set({ loading: false })
      }
      return result
    } catch (error: unknown) {
      const errorResponse = error as {
        response?: {
          data?: {
            errors?: string[]
          }
        }
        message?: string
      }

      const errorMessage = errorResponse.response?.data?.errors || (errorResponse.message ? [errorResponse.message] : ['Unknown error'])

      set({ loading: false, error: errorMessage })
      logError('Registration step one failed:', { error: error as Record<string, unknown> })
      return Promise.reject(error)
    }
  },

  registrationStepTwo: async (_isDraft = false) => {
    const state = get()
    const currentBrandData = useAccessStore.getState().brandData
    const brandDataId = currentBrandData?.id
    let userId = brandDataId || state.brandUser?.id || state.id

    if (!userId) {
      try {
        const brandData = await useAccessStore.getState().fetchBrandData()

        if (brandData?.id) {
          userId = brandData.id

          set(
            (state) =>
              ({
                ...state,
                id: brandData.id,
                brandUser: {
                  ...state.brandUser,
                  id: brandData.id,
                },
              }) as BrandUserState,
          )
        } else {
          logError('Brand data returned without ID field', { brandData })
        }
      } catch (error) {
        console.error('Error fetching brand data:', error)
        logError('Failed to fetch brand data for ID', { error })
      }
    }

    if (!userId) {
      const errorMsg = 'User ID is not found. Please complete step one first.'
      console.error(errorMsg)
      logError(errorMsg, {
        stateId: state.id,
        brandUserId: state.brandUser?.id,
        brandDataId,
      })
      set({
        loading: false,
        error: [errorMsg],
      })
      return null
    }

    try {
      set({ loading: true, error: [] })

      const formData = {
        id: userId,
        jobPosition: state.brandUser?.jobPosition || '',
        phoneNumber: state.brandUser?.phoneNumber || '',
        companyName: state.brandUser?.companyName || '',
        region: state.brandUser?.region || '',
        industries: state.brandUser?.industries || [],
        annualBudget: state.brandUser?.annualBudget || '',
        companySize: state.brandUser?.companySize || '',
        additionalInformation: state.brandUser
          ? typeof state.brandUser.additionalInformation === 'object'
            ? ''
            : state.brandUser.additionalInformation || ''
          : '',
      }

      const result = await brandUserService.registerStepTwo(formData)

      if (result?.id) {
        set(
          (state) =>
            ({
              ...state,
              id: result.id,
              brandUser: {
                ...result,
              },
            }) as BrandUserState,
        )
      } else {
        console.warn('Result does not contain ID:', result)
      }

      set({ loading: false })
      try {
        await useAccessStore.getState().checkRegistrationStatus()
      } catch (error) {
        console.error('Error updating registration status:', error)
      }

      return { success: true, data: result }
    } catch (error: unknown) {
      const errorResponse = error as {
        response?: {
          data?: {
            data?: Record<string, string | string[]>
            message?: string
          }
        }
        message?: string
      }

      const responseData = errorResponse.response?.data
      const errorMessages = []

      if (responseData?.data) {
        for (const [key, message] of Object.entries(responseData.data)) {
          const fieldName = key.charAt(0).toUpperCase() + key.slice(1)
          errorMessages.push(`${fieldName}: ${message}`)
        }
      } else if (responseData?.message) {
        errorMessages.push(responseData.message)
      } else {
        errorMessages.push(errorResponse.message || 'An error occurred during registration')
      }

      set({ loading: false, error: errorMessages })
      logError('Registration step two failed:', { error: error as Record<string, unknown> })
      return Promise.reject(error)
    }
  },

  fetchBrandUser: async () => {
    try {
      set({ loading: true, error: [] })
      const brandUser = await brandUserService.getBrandUser()
      set({ brandUser, loading: false })
      return brandUser
    } catch (error: unknown) {
      const errorResponse = error as {
        response?: {
          data?: {
            errors?: string[]
          }
        }
        message?: string
      }

      const errorMessage = errorResponse.response?.data?.errors || (errorResponse.message ? [errorResponse.message] : ['Unknown error'])

      set({ loading: false, error: errorMessage })
      logError('Failed to fetch brand user:', { error: error as Record<string, unknown> })
      return Promise.reject(error)
    }
  },

  updateBrandUser: async (userData) => {
    try {
      set({ loading: true, error: [] })
      const updatedUser = await brandUserService.updateBrandUser(userData)
      const latestUserData = await brandUserService.getBrandUser()
      set({ brandUser: latestUserData, loading: false })
      return updatedUser
    } catch (error: unknown) {
      const errorResponse = error as {
        response?: {
          data?: {
            errors?: string[]
          }
        }
        message?: string
      }

      const errorMessage = errorResponse.response?.data?.errors || (errorResponse.message ? [errorResponse.message] : ['Unknown error'])

      set({ loading: false, error: errorMessage })
      logError('Failed to update brand user:', { error: error as Record<string, unknown> })
      return Promise.reject(error)
    }
  },
})

const useBrandUserStore = createPersistedStore<BrandUserState, BrandUserActions>('brand-user-store', initialState, actions)

export default useBrandUserStore
